import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Box, Button, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RCBarChart from '../../components/charts/recharts/rcBarChart';

/**
 * Data shape returned by `ScanLevel/GetScanLevelStats`.
 * `id` carries the market-type string (mapped from the C# `Id` / `MarketType` field).
 */
interface ScanLevelStat {
    /** Market type identifier (e.g. "TN", "Vivid", "exact"). */
    id: string;
    /** Count of events whose `InsertedOn` falls within the last 7 days. */
    newEvents7d: number;
    /** Count of events whose `InsertedOn` falls within the last 14 days. */
    newEvents14d: number;
    /** Count of events whose `InsertedOn` falls within the last 30 days. */
    newEvents30d: number;
    /**
     * Most recent `InsertedOn` date in the 30-day window (date only — time stripped on the backend).
     * ISO-8601 string, e.g. `"2026-06-24T00:00:00"`. `null` when no rows exist.
     */
    latestDate: string | null;
    /** Count of events whose `InsertedOn` date matches `latestDate` (new events from the most recent scan — the "Latest Day" period). */
    latestCount: number;
}

/**
 * Custom tooltip rendered by {@link RCBarChart} when the user hovers a bar group.
 * Shows `latestCount`, `newEvents7d`, `newEvents14d`, `newEvents30d`, and `latestDate` for the hovered market type.
 */
const ScanLevelTooltip: React.FC<{
    active?: boolean;
    payload?: Array<{ name: string; value: number; fill: string; payload?: ScanLevelStat }>;
    label?: string;
}> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const stat = payload[0]?.payload;
    const latestDateStr = stat?.latestDate
        ? new Date(stat.latestDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
        : '—';

    return (
        <TableContainer component={Paper} sx={{ minWidth: 220 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{label}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {payload.map((row) => (
                        <TableRow key={row.name}>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: row.fill, flexShrink: 0 }} />
                                    {row.name}
                                </Box>
                            </TableCell>
                            <TableCell align="right">{row.value.toLocaleString()}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Latest Date</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{latestDateStr}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/**
 * Scan-Level tab of the Inventory Drop Analysis page.
 *
 * Fetches per-market new-event counts from the `ScanLevel/GetScanLevelStats`
 * endpoint and visualises them as a grouped bar chart using {@link RCBarChart},
 * following the same `seriesList` pattern as the Users Dashboard's bar chart
 * (`src/pages/user-dashboard/index.js`) — one entry per series with a `fill`
 * color and a white-filled `labelList` for on-bar value labels.
 * Each market type appears on the X-axis with four bars:
 * - **Latest Day** — events inserted on the most recent scan date (`LatestDate`).
 * - **7 days** — events inserted in the last 7 days.
 * - **14 days** — events inserted in the last 14 days.
 * - **30 days** — events inserted in the last 30 days.
 *
 * A **Latest Date** (date only) is surfaced in the bar-chart tooltip for each market type.
 *
 * A **Refresh** button allows the user to reload data without leaving the tab,
 * consistent with the keep-alive tab strategy in `index.tsx`.
 *
 * Exact-market data is included in the results following the removal of "exact"
 * from the backend exclusion filter.
 */
const ScanLevelView: React.FC = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [data, setData] = useState<ScanLevelStat[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    /** Incrementing this value triggers a data reload via `useEffect`. */
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        dataProvider
            .fetchData('ScanLevel', 'GetScanLevelStats')
            .then((response: { data?: unknown }) => {
                if (Array.isArray(response?.data)) {
                    setData(response.data as ScanLevelStat[]);
                }
            })
            .catch((err: Error) => {
                notify(`Failed to load scan-level stats: ${err?.message}`, {
                    type: 'error',
                    autoHideDuration: 5000,
                });
            })
            .finally(() => setIsLoading(false));
    }, [dataProvider, notify]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshKey]);

    const handleRefresh = () => setRefreshKey((k) => k + 1);

    return (
        <Box sx={{ p: 2 }}>
            {/* Header row: title + refresh button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h5">Scan-Level New Events by Market</Typography>
                <Tooltip title="Reload scan-level stats">
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={isLoading}
                        >
                            Refresh
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {isLoading && <LinearProgress color="secondary" />}

            {!isLoading && data.length === 0 && (
                <Typography color="text.secondary">No scan-level data available.</Typography>
            )}

            {data.length > 0 && (
                /* Bar chart — hover any bar to see LatestDate in the tooltip */
                <Box sx={{ overflowX: 'auto' }}>
                    <RCBarChart
                        data={data}
                        width={Math.max(800, data.length * 110)}
                        height={500}
                        xAxis={{ dataKey: 'id' }}
                        yAxis={{ position: 'insideLeft' }}
                        legend={{ verticalAlign: 'top', height: 60, iconType: 'square' }}
                        barSize={60}
                        showTotal
                        yGap={2}
                        barGap={0}
                        customTooltip={ScanLevelTooltip}
                        seriesList={[
                            {
                                dataKey: 'latestCount',
                                name: 'Latest Events',
                                fill: '#43a047',
                                stroke: '#43a047',
                                strokeWidth: 0,
                                stackId: 'newEvents',
                                labelList: { fill: 'white' },
                            },
                            {
                                dataKey: 'newEvents7d',
                                name: 'New Events (7d)',
                                fill: '#1976d2',
                                stroke: '#1976d2',
                                strokeWidth: 0,
                                stackId: 'newEvents',
                                labelList: { fill: 'white' },
                            },
                            {
                                dataKey: 'newEvents14d',
                                name: 'New Events (14d)',
                                fill: '#ff9800',
                                stroke: '#ff9800',
                                strokeWidth: 0,
                                stackId: 'newEvents',
                                labelList: { fill: 'white' },
                            },
                            {
                                dataKey: 'newEvents30d',
                                name: 'New Events (30d)',
                                fill: '#8e24aa',
                                stroke: '#8e24aa',
                                strokeWidth: 0,
                                stackId: 'newEvents',
                                labelList: { fill: 'white' },
                            },
                        ]}
                        legendStyle={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            paddingTop: '0px',
                            marginTop: '0px'
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ScanLevelView;
